/**
 * 統合給与 v4.1.0
 *
 * これまで給与が2箇所にあった。
 *   - Firestore側: 勤怠・シフト・ドリンクバック・指名・累進インセンティブ・日払い
 *   - NightPro側 : 時給・日給・歩合・手当バック・給与レポート
 * 同じキャストが両方に別IDで存在し、どちらの画面を見ても支給額が出せなかった。
 *
 * このファイルが唯一の給与計算になる。
 *   支給額 = 基本給 + バック + 手当バック + 歩合 − 日払い ± 調整
 *
 * 各項目の出どころ:
 *   基本給   … attendances の実労働時間 × 時給 / 日給 × 出勤日数 / 月給（在籍日数で按分）
 *   バック   … staffIncentives.progressiveBackAmount（累進）or backAmount（商品バック）
 *   手当バック … staffIncentives.breakdown.allowanceBack（同伴・ボトルバック等）
 *   歩合     … 当期売上 × commissionRate
 *   日払い   … payouts の合計
 *   調整     … backAdjustments の合計
 */

export type PaymentType = 'hourly' | 'daily' | 'monthly' | 'none';
export type CommissionBase = 'sales' | 'base' | 'none';

export interface PayrollStaff {
  id: string;
  name: string;
  role?: string;
  paymentType: PaymentType;
  hourlyWage?: number;
  dailyWage?: number;
  monthlyWage?: number;
  /** 歩合率（0-1）。売上に対して掛ける */
  commissionRate?: number;
  /** 歩合の母数。'sales'=売上, 'base'=分配対象額, 'none'=歩合なし */
  commissionBase?: CommissionBase;
  /** 在籍日数（月給の按分に使う） */
  employedDays?: number;
  /** バックを支給するか。false なら基本給と歩合のみ */
  backEnabled?: boolean;
}

export interface PayrollAttendance {
  /** 実労働時間の合計（分） */
  actualWorkMinutes: number;
  /** 出勤日数（営業日ベース） */
  workedDays: number;
  lateCount: number;
  lateMinutes: number;
  earlyLeaveCount: number;
  earlyLeaveMinutes: number;
  absentDays: number;
}

export interface PayrollSales {
  /** 当期の売上（税基準変換後） */
  sales: number;
  /** 当期の分配対象額 */
  base: number;
  /** 商品・指名・プランのバック */
  back: number;
  /** 手当バック */
  allowanceBack: number;
}

export interface PayrollDeductions {
  /** 日払いで先に渡した額 */
  paidOut: number;
  /** バック調整の合計（プラスもマイナスもある） */
  adjustments: number;
  /** 遅刻・早退の控除 */
  lateDeduction: number;
  /** その他の控除（寮費・送り代など） */
  other: number;
  otherLabel?: string;
}

export interface PayrollRow {
  staffId: string;
  staffName: string;
  role?: string;

  workedDays: number;
  workedHours: number;

  basePay: number;
  basePayBasis: string;

  back: number;
  allowanceBack: number;
  commission: number;
  commissionBasis: string;

  grossPay: number;

  paidOut: number;
  adjustments: number;
  lateDeduction: number;
  otherDeduction: number;

  /** 締めで実際に渡す額 */
  netPay: number;

  sales: number;
  /** 人件費率（対売上）。売上0なら null */
  laborRatio: number | null;

  warnings: string[];
}

export interface PayrollOptions {
  /** 期間の日数（月給の按分に使う） */
  periodDays: number;
  /** 遅刻・早退を控除するか */
  deductLate?: boolean;
}

/** 1人ぶんの給与を組み立てる */
export function calculatePayroll(
  staff: PayrollStaff,
  attendance: PayrollAttendance,
  sales: PayrollSales,
  deductions: PayrollDeductions,
  options: PayrollOptions
): PayrollRow {
  const warnings: string[] = [];
  const hours = attendance.actualWorkMinutes / 60;

  // ---- 基本給 ----
  let basePay = 0;
  let basePayBasis = '';
  switch (staff.paymentType) {
    case 'hourly': {
      const w = staff.hourlyWage ?? 0;
      if (w <= 0) warnings.push('時給が未設定です');
      basePay = Math.floor(hours * w);
      basePayBasis = `時給 ¥${w.toLocaleString()} × ${hours.toFixed(1)}時間`;
      break;
    }
    case 'daily': {
      const w = staff.dailyWage ?? 0;
      if (w <= 0) warnings.push('日給が未設定です');
      basePay = Math.floor(attendance.workedDays * w);
      basePayBasis = `日給 ¥${w.toLocaleString()} × ${attendance.workedDays}日`;
      break;
    }
    case 'monthly': {
      const w = staff.monthlyWage ?? 0;
      if (w <= 0) warnings.push('月給が未設定です');
      const employed = staff.employedDays ?? options.periodDays;
      const ratio = options.periodDays > 0 ? Math.min(1, employed / options.periodDays) : 1;
      basePay = Math.floor(w * ratio);
      basePayBasis = ratio >= 1
        ? `月給 ¥${w.toLocaleString()}（満額）`
        : `月給 ¥${w.toLocaleString()} × ${employed}/${options.periodDays}日（日割り）`;
      break;
    }
    default:
      warnings.push('給与形態が未設定です。基本給を計算できません');
      basePayBasis = '未設定';
  }

  // ---- バック ----
  const backEnabled = staff.backEnabled !== false;
  const back = backEnabled ? sales.back : 0;
  const allowanceBack = backEnabled ? sales.allowanceBack : 0;
  if (!backEnabled && (sales.back > 0 || sales.allowanceBack > 0)) {
    warnings.push('バック対象外の設定のため、発生したバックを支給していません');
  }

  // ---- 歩合 ----
  let commission = 0;
  let commissionBasis = 'なし';
  const cBase = staff.commissionBase ?? 'none';
  const cRate = staff.commissionRate ?? 0;
  if (cBase !== 'none' && cRate > 0) {
    const src = cBase === 'sales' ? sales.sales : sales.base;
    commission = Math.floor(src * cRate);
    commissionBasis = `${cBase === 'sales' ? '売上' : '分配対象額'} ¥${src.toLocaleString()} × ${Math.round(cRate * 1000) / 10}%`;
  }

  const grossPay = basePay + back + allowanceBack + commission;

  // ---- 控除 ----
  const lateDeduction = options.deductLate ? deductions.lateDeduction : 0;
  const netPay = grossPay + deductions.adjustments - deductions.paidOut - lateDeduction - deductions.other;

  if (netPay < 0) {
    warnings.push(`支給額がマイナス（¥${netPay.toLocaleString()}）です。日払いの渡しすぎか控除の入れすぎを確認してください`);
  }
  if (deductions.paidOut > grossPay) {
    warnings.push(`日払い ¥${deductions.paidOut.toLocaleString()} が支給総額 ¥${grossPay.toLocaleString()} を超えています`);
  }
  if (attendance.workedDays === 0 && grossPay > 0) {
    warnings.push('出勤記録が無いのに支給額があります。勤怠の打刻漏れの可能性があります');
  }

  return {
    staffId: staff.id,
    staffName: staff.name,
    role: staff.role,
    workedDays: attendance.workedDays,
    workedHours: Math.round(hours * 10) / 10,
    basePay,
    basePayBasis,
    back,
    allowanceBack,
    commission,
    commissionBasis,
    grossPay,
    paidOut: deductions.paidOut,
    adjustments: deductions.adjustments,
    lateDeduction,
    otherDeduction: deductions.other,
    netPay,
    sales: sales.sales,
    laborRatio: sales.sales > 0 ? Math.round((grossPay / sales.sales) * 1000) / 10 : null,
    warnings,
  };
}

export interface PayrollTotals {
  staffCount: number;
  basePay: number;
  back: number;
  allowanceBack: number;
  commission: number;
  grossPay: number;
  paidOut: number;
  adjustments: number;
  netPay: number;
  sales: number;
  laborRatio: number | null;
  warningCount: number;
}

export function summarizePayroll(rows: PayrollRow[]): PayrollTotals {
  const sum = (f: (r: PayrollRow) => number) => rows.reduce((s, r) => s + f(r), 0);
  const sales = sum((r) => r.sales);
  const grossPay = sum((r) => r.grossPay);
  return {
    staffCount: rows.length,
    basePay: sum((r) => r.basePay),
    back: sum((r) => r.back),
    allowanceBack: sum((r) => r.allowanceBack),
    commission: sum((r) => r.commission),
    grossPay,
    paidOut: sum((r) => r.paidOut),
    adjustments: sum((r) => r.adjustments),
    netPay: sum((r) => r.netPay),
    sales,
    laborRatio: sales > 0 ? Math.round((grossPay / sales) * 1000) / 10 : null,
    warningCount: rows.reduce((s, r) => s + r.warnings.length, 0),
  };
}

/** 明細に出す「なぜこの額なのか」 */
export function describePayroll(r: PayrollRow): string {
  const parts = [`基本給 ¥${r.basePay.toLocaleString()}（${r.basePayBasis}）`];
  if (r.back > 0) parts.push(`バック ¥${r.back.toLocaleString()}`);
  if (r.allowanceBack > 0) parts.push(`手当バック ¥${r.allowanceBack.toLocaleString()}`);
  if (r.commission > 0) parts.push(`歩合 ¥${r.commission.toLocaleString()}（${r.commissionBasis}）`);
  let s = parts.join(' ＋ ') + ` ＝ ¥${r.grossPay.toLocaleString()}`;
  // 調整はプラスにもマイナスにもなる。符号に合わせて置く場所を変える。
  if (r.adjustments > 0) s += ` ＋ 調整 ¥${r.adjustments.toLocaleString()}`;
  const minus: string[] = [];
  if (r.adjustments < 0) minus.push(`調整 ¥${Math.abs(r.adjustments).toLocaleString()}`);
  if (r.paidOut > 0) minus.push(`日払い ¥${r.paidOut.toLocaleString()}`);
  if (r.lateDeduction > 0) minus.push(`遅刻控除 ¥${r.lateDeduction.toLocaleString()}`);
  if (r.otherDeduction > 0) minus.push(`その他控除 ¥${r.otherDeduction.toLocaleString()}`);
  if (minus.length) s += ` − ${minus.join(' − ')}`;
  return `${s} ＝ 支給 ¥${r.netPay.toLocaleString()}`;
}
